import { IconNames } from "@blueprintjs/icons";
import { useCurrentProjectId } from "in_queue/contexts/ScenarioPageContext";
import { useScenarioResults } from "in_queue/contexts/ScenarioResultsContext";
import { ALLOCATED_COST_WITHOUT_SINCS_KEY } from "in_queue/types/scenarioResultType";
import { formatCosts } from "in_queue/utils/formatting";
import { useMemo } from "react";
import { Loading, mapLoadingState } from "types/loadingType";
import { RichMetricCard } from "../common/MetricCard";
import css from "./ScenarioMetrics.module.scss";

export const ScenarioMetrics: React.FC = () => {
    const projectId = useCurrentProjectId();
    const metrics = useScenarioMetrics(projectId);
    const sumAllocatedCostsWithoutSincUpgrades = mapLoadingState(
        metrics,
        (m) => m.sumAllocatedCostsWithoutSincUpgrades ?? null
    );
    const rankDescription =
        metrics !== "loading" && metrics.rank.sub !== ""
            ? metrics.rank.sub
            : "Against every project in this scenario. P1 is cheapest.";

    return (
        <div className={css["ScenarioMetrics"]}>
            <RichMetricCard
                icon={IconNames.DOLLAR}
                label="Sum of allocated costs"
                value={mapLoadingState(metrics, (m) => m.sumAllocatedCosts)}
                description="Network upgrade cost allocated to this project in this scenario."
            />
            {sumAllocatedCostsWithoutSincUpgrades !== "loading" &&
                sumAllocatedCostsWithoutSincUpgrades !== null && (
                    <RichMetricCard
                        icon={IconNames.LAYERS}
                        label="Allocated costs excl. SINC"
                        value={sumAllocatedCostsWithoutSincUpgrades}
                        description="The same allocation with SINC upgrades taken out."
                    />
                )}
            <RichMetricCard
                icon={IconNames.WRENCH}
                label="Number of upgrades"
                value={mapLoadingState(metrics, (m) => m.numUpgrades)}
                description="Only includes upgrades carrying an allocated cost."
            />
            <RichMetricCard
                icon={IconNames.CALCULATOR}
                label="$ / MW"
                value={mapLoadingState(metrics, (m) => m.dollarsPerMw)}
                description="Allocated cost divided by this project's size in this scenario."
            />
            <RichMetricCard
                icon={IconNames.SORT_ASC}
                label="Rank ($/MW)"
                value={mapLoadingState(metrics, (m) => m.rank.percentile)}
                description={rankDescription}
            />
        </div>
    );
};

interface ScenarioMetrics {
    sumAllocatedCosts: string;
    sumAllocatedCostsWithoutSincUpgrades?: string;
    numUpgrades: string;
    dollarsPerMw: string;
    rank: ScenarioRank;
}

interface ScenarioRank {
    /** The card's value: "p14", or "-" when the scenario did not rank it. */
    percentile: string;
    /** The card's description: "14 of 100", empty when unranked. */
    sub: string;
}

const UNRANKED: ScenarioRank = { percentile: "-", sub: "" };

const useScenarioMetrics = (
    currentProjectId: string
): Loading<ScenarioMetrics> => {
    const { scenarioResults } = useScenarioResults();

    return useMemo(() => {
        return mapLoadingState(scenarioResults, (details) => {
            const currentProjectResults = details.projects.find(
                (project) => project.projectId === currentProjectId
            );
            if (currentProjectResults === undefined) {
                return {
                    sumAllocatedCosts: "-",
                    numUpgrades: "-",
                    dollarsPerMw: "-",
                    rank: UNRANKED
                };
            }
            const numUpgrades = new Set(
                details.allocatedUpgrades
                    .filter(
                        (upgrade) =>
                            upgrade.projectId === currentProjectId &&
                            upgrade.allocatedCost > 0
                    )
                    .map((upgrade) => upgrade.networkUpgradeId)
            ).size;

            const sumAllocatedCostsWithoutSincUpgrades =
                currentProjectResults.metadata[
                    ALLOCATED_COST_WITHOUT_SINCS_KEY
                ];

            const { rank, rankPercentile, totalRanked } = currentProjectResults;

            const memo: ScenarioMetrics = {
                sumAllocatedCosts: formatCosts(
                    currentProjectResults.sumAllocatedCost
                ),
                numUpgrades: numUpgrades.toFixed(0),
                dollarsPerMw: formatCosts(currentProjectResults.costPerMw),
                rank:
                    rank !== undefined &&
                    rankPercentile !== undefined &&
                    totalRanked !== undefined &&
                    totalRanked > 0
                        ? {
                              percentile: `p${rankPercentile}`,
                              sub: `${rank} of ${totalRanked}`
                          }
                        : UNRANKED
            };

            if (sumAllocatedCostsWithoutSincUpgrades !== undefined) {
                memo.sumAllocatedCostsWithoutSincUpgrades = formatCosts(
                    parseFloat(sumAllocatedCostsWithoutSincUpgrades)
                );
            }

            return memo;
        });
    }, [scenarioResults, currentProjectId]);
};
